import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState
} from "react";

import { useAuth }
    from "../../contexts/AuthContext";


const CartContext =
    createContext();


const STORAGE_KEY =
    "myfavv_marketplace_cart";


export function CartProvider({
    children
}) {

    const { user } =
        useAuth();


    const [items, setItems] =
        useState(() => {

            try {

                const saved =
                    localStorage.getItem(
                        STORAGE_KEY
                    );

                if (!saved) {
                    return [];
                }

                const parsed =
                    JSON.parse(saved);

                return Array.isArray(
                    parsed
                )
                    ? parsed
                    : [];

            }

            catch {

                return [];

            }

        });


    /*
    --------------------------------------------------
    SAVE CART LOCALLY
    --------------------------------------------------
    */

    useEffect(() => {

        try {

            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify(items)
            );

        }

        catch (error) {

            console.error(
                "Failed to save cart:",
                error
            );

        }

    }, [items]);


    /*
    --------------------------------------------------
    ADD TO CART
    --------------------------------------------------
    */

    const addToCart = (
        product,
        quantity = 1,
        variant = null
    ) => {

        if (!product?.id) {

            throw new Error(
                "Product is required."
            );

        }


        const safeQuantity =
            Math.max(
                1,
                Number(quantity) || 1
            );


        const variantId =
            variant?.id || null;


        const unitPrice =
            Number(
                variant?.price ??
                product.price ??
                0
            );


        const image =
            Array.isArray(
                product.images
            )

                ? product.images[0] || null

                : product.images || null;


        const cartKey =
            `${product.id}:${variantId || "default"}`;


        setItems(previous => {

            const existing =
                previous.find(
                    item =>
                        item.cartKey === cartKey
                );


            if (existing) {

                return previous.map(
                    item =>

                        item.cartKey === cartKey

                            ? {
                                ...item,
                                quantity:
                                    item.quantity +
                                    safeQuantity
                            }

                            : item
                );

            }


            return [

                ...previous,

                {

                    cartKey,

                    productId:
                        product.id,

                    variantId,

                    brandId:
                        product.brand || null,

                    title:
                        product.title ||
                        "Untitled Product",

                    quantity:
                        safeQuantity,

                    unitPrice,

                    currency:
                        product.currency ||
                        "INR",

                    image,

                    sku:
                        variant?.sku ||
                        product.sku ||
                        "",

                    variant:
                        variant
                            ? {
                                id:
                                    variant.id,

                                title:
                                    variant.title ||
                                    variant.name ||
                                    "Variant"
                            }
                            : null

                }

            ];

        });

    };


    /*
    --------------------------------------------------
    UPDATE QUANTITY
    --------------------------------------------------
    */

    const updateQuantity = (
        cartKey,
        quantity
    ) => {

        const safeQuantity =
            Math.max(
                1,
                Number(quantity) || 1
            );


        setItems(previous =>

            previous.map(item =>

                item.cartKey === cartKey

                    ? {
                        ...item,
                        quantity:
                            safeQuantity
                    }

                    : item

            )

        );

    };


    /*
    --------------------------------------------------
    REMOVE
    --------------------------------------------------
    */

    const removeFromCart = (
        cartKey
    ) => {

        setItems(previous =>

            previous.filter(
                item =>
                    item.cartKey !==
                    cartKey
            )

        );

    };


    /*
    --------------------------------------------------
    CLEAR
    --------------------------------------------------
    */

    const clearCart = () => {

        setItems([]);

    };


    /*
    --------------------------------------------------
    TOTAL ITEMS
    --------------------------------------------------
    */

    const itemCount =
        useMemo(() =>

            items.reduce(

                (
                    total,
                    item
                ) =>

                    total +
                    Number(
                        item.quantity || 0
                    ),

                0

            ),

        [items]);


    /*
    --------------------------------------------------
    SUBTOTAL
    --------------------------------------------------
    */

    const subtotal =
        useMemo(() =>

            items.reduce(

                (
                    total,
                    item
                ) =>

                    total +

                    (
                        Number(
                            item.unitPrice || 0
                        ) *

                        Number(
                            item.quantity || 0
                        )
                    ),

                0

            ),

        [items]);


    /*
    --------------------------------------------------
    CART VALUE
    --------------------------------------------------
    */

    const value = {

        items,

        itemCount,

        subtotal,

        addToCart,

        updateQuantity,

        removeFromCart,

        clearCart

    };


    return (

        <CartContext.Provider
            value={value}
        >
            {children}
        </CartContext.Provider>

    );

}


export function useCart() {

    return useContext(
        CartContext
    );

}